import { useRef } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Trash2, Plus, Upload, Loader2 } from 'lucide-react';

const GalleryEditor = ({ data, setData, uploadImage, uploading }) => {
  const galleryFileRef = useRef(null);
  const groups = data.groups || [{ title: '', items: data.items || [] }];
  const captionSettings = data.captionSettings || { fontSize: 12, align: 'center' };

  const updateGroups = (newGroups) => setData({ ...data, groups: newGroups });
  const updateCaptionSettings = (key, val) => setData({ ...data, captionSettings: { ...captionSettings, [key]: val } });

  const addImageToGroup = (gi) => {
    galleryFileRef.current?.click();
    galleryFileRef.current._groupIndex = gi;
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">قسّم الصور حسب الأحداث. كل مجموعة لها عنوان وصور خاصة بها.</p>

      {/* Caption Settings */}
      <Card className="p-4 space-y-3 bg-muted/30 border-dashed">
        <Label className="text-sm font-bold text-accent">إعدادات النص على الصور</Label>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <Label className="text-xs text-muted-foreground">حجم الخط (px)</Label>
            <Input type="number" min={8} max={24} value={captionSettings.fontSize}
              onChange={e => updateCaptionSettings('fontSize', parseInt(e.target.value) || 12)}
              className="w-24 mt-1" data-testid="caption-font-size" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">محاذاة النص</Label>
            <div className="flex gap-1 mt-1">
              {[
                { value: 'right', label: 'يمين' },
                { value: 'center', label: 'وسط' },
                { value: 'left', label: 'يسار' },
                { value: 'justify', label: 'ضبط' },
              ].map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => updateCaptionSettings('align', opt.value)}
                  className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${captionSettings.align === opt.value ? 'bg-primary text-white border-primary' : 'bg-white border-gray-200 hover:border-primary/50'}`}
                  data-testid={`caption-align-${opt.value}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {groups.map((group, gi) => (
        <Card key={gi} className="p-4 space-y-3 overflow-hidden">
          <div className="flex items-center gap-3">
            <Input value={group.title || ''} onChange={e => { const n = [...groups]; n[gi] = { ...n[gi], title: e.target.value }; updateGroups(n); }}
              placeholder={`اسم الحدث (مثال: اللقاء الأول)`} className="flex-1" data-testid={`gallery-group-title-${gi}`} />
            <button onClick={() => updateGroups(groups.filter((_, j) => j !== gi))} className="text-red-400 hover:text-red-600"><Trash2 className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {(group.items || []).map((item, ii) => (
              <div key={ii} className="relative group/img rounded-xl overflow-hidden border aspect-[4/3]">
                <img src={item.url} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button onClick={() => { const n = [...groups]; n[gi] = { ...n[gi], items: (n[gi].items || []).filter((_, j) => j !== ii) }; updateGroups(n); }}
                    className="bg-red-500 text-white p-1.5 rounded-full"><Trash2 className="w-4 h-4" /></button>
                </div>
                {item.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white px-1.5 py-0.5 text-[10px] truncate text-center">{item.caption}</div>
                )}
              </div>
            ))}
            <div onClick={() => addImageToGroup(gi)}
              className="aspect-[4/3] border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors">
              {uploading ? <Loader2 className="w-6 h-6 animate-spin text-primary" /> : <><Upload className="w-6 h-6 text-gray-400" /><p className="text-[10px] text-gray-500 mt-1">رفع صورة</p></>}
            </div>
          </div>
        </Card>
      ))}
      <Button variant="outline" onClick={() => updateGroups([...groups, { title: '', items: [] }])} className="w-full" data-testid="add-gallery-group">
        <Plus className="w-4 h-4 ml-2" /> إضافة مجموعة صور
      </Button>
      <input ref={galleryFileRef} type="file" accept="image/*" className="hidden" onChange={e => {
        const gi = galleryFileRef.current._groupIndex ?? 0;
        uploadImage(e.target.files?.[0], url => {
          const n = [...groups];
          if (!n[gi]) n[gi] = { title: '', items: [] };
          n[gi] = { ...n[gi], items: [...(n[gi].items || []), { url, caption: '' }] };
          updateGroups(n);
          if(galleryFileRef.current) galleryFileRef.current.value='';
        });
      }} />
    </div>
  );
};

export default GalleryEditor;
